import { z } from 'zod';
import { defineCollection, type RecordOf } from '../collection.ts';
import { id, isoDate, longText, requiredText, shortText } from '../fields.ts';

export const PET_SPECIES = ['dog', 'cat', 'bird', 'fish', 'rabbit', 'reptile', 'small_mammal', 'horse', 'other'] as const;

const contactBlock = z.object({
  name: shortText(120).default(''),
  clinic: shortText(160).default(''),
  phone: shortText(40).default(''),
  email: shortText(200).default(''),
  address: shortText(300).default(''),
});

export const pets = defineCollection({
  name: 'pets',
  module: 'pets',
  label: { one: 'pet', other: 'pets' },
  schema: z.object({
    name: requiredText(80),
    species: z.enum(PET_SPECIES).default('dog'),
    breed: shortText(100).default(''),
    birthDate: isoDate.nullable().default(null),
    /** When the exact birthday is unknown. */
    approxBirthYear: z.number().int().min(1950).max(2100).nullable().default(null),
    sex: z.enum(['female', 'male', 'unknown']).default('unknown'),
    markings: shortText(160).default(''),
    microchipId: shortText(64).default(''),
    photoFileId: id.nullable().default(null),
    vet: contactBlock.default({ name: '', clinic: '', phone: '', email: '', address: '' }),
    insurance: z
      .object({
        provider: shortText(160).default(''),
        policyNumber: shortText(80).default(''),
        phone: shortText(40).default(''),
      })
      .default({ provider: '', policyNumber: '', phone: '' }),
    /** Shown on the Emergency screen: allergies, behavior, feeding essentials. */
    emergencyNotes: shortText(2_000).default(''),
    notes: longText(10_000).default(''),
    archived: z.boolean().default(false),
  }),
  defaultAudience: 'family',
  childAccess: 'read',
  editPolicy: 'collaborative',
  immutableFields: [],
  manualMergeFields: ['notes', 'emergencyNotes'],
});

export const PET_RECORD_KINDS = ['medication', 'vaccination', 'visit', 'weight', 'note'] as const;

/**
 * Health records are reference information and reminders only — the app never
 * interprets them or gives medical guidance. Appointments are calendar events
 * related to the pet, not records here.
 */
export const petRecords = defineCollection({
  name: 'pet_records',
  module: 'pets',
  label: { one: 'pet record', other: 'pet records' },
  schema: z.object({
    petId: id,
    kind: z.enum(PET_RECORD_KINDS),
    title: requiredText(200),
    /** Date given / visit date / start date. */
    date: isoDate.nullable().default(null),
    /** Next due date (vaccination booster, refill...). */
    dueDate: isoDate.nullable().default(null),
    endDate: isoDate.nullable().default(null),
    dosage: shortText(200).default(''),
    schedule: shortText(200).default(''),
    provider: shortText(200).default(''),
    notes: shortText(5_000).default(''),
  }),
  parent: { kind: 'fixed', collection: 'pets', field: 'petId' },
  defaultAudience: 'family',
  childAccess: 'read',
  editPolicy: 'collaborative',
  immutableFields: ['petId'],
  manualMergeFields: ['notes'],
});

export type Pet = RecordOf<typeof pets>;
export type PetRecord = RecordOf<typeof petRecords>;
